import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BadgeCheck, ArrowRight, ShieldCheck, Package, Truck, Receipt, Mail, X, Loader2 } from "lucide-react";
import { PLATFORM_FEE_RATE } from "@/lib/payments";
import TranslatedText from "@/components/TranslatedText";

export default function ProductView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then(r=>setP(r.data)).catch(e=>setErr(formatErr(e)));
    api.get(`/products/${id}/quote`).then(r=>setQuote(r.data)).catch(()=>setQuote(null));
  }, [id]);

  // Kicks off Stripe Checkout — optionally as a guest by passing an email.
  const startCheckout = async (guestEmail) => {
    setBusy(true); setErr("");
    try {
      const body = { product_id: id, origin_url: window.location.origin };
      if (guestEmail) body.guest_email = guestEmail;
      const { data } = await api.post("/products/checkout", body);
      window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); setGuestOpen(false); }
  };

  const buy = () => {
    // Logged-in users skip the email prompt; guests get a small modal.
    if (user) return startCheckout();
    setGuestOpen(true);
  };

  if (err) return <div className="p-12 text-center text-[#B84B4B]" data-testid="product-error">{err}</div>;
  if (!p) return <div className="p-12 text-center text-[#8A8A8A]" data-testid="product-loading">Loading…</div>;

  const isPhysical = p.product_type === "physical";
  // Under the tip-on-top model the backend returns `platform_fee` inside the quote.
  const subtotal = Number(quote?.subtotal ?? p.price ?? 0);
  const shipping = Number(quote?.shipping ?? 0);
  const tax = Number(quote?.tax ?? 0);
  const fee = Number(quote?.platform_fee ?? Math.round((subtotal + shipping) * PLATFORM_FEE_RATE * 100) / 100);
  const total = Number(quote?.total ?? (subtotal + shipping + tax + fee));

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10 grid lg:grid-cols-[1fr_400px] gap-10" data-testid="product-page">
      <div>
        <ProductGallery product={p}/>

        {/* Shipping / delivery info card — physical only */}
        {isPhysical && (p.ships_from || p.ships_to || p.estimated_delivery) && (
          <div className="card p-5 mt-6" data-testid="product-shipping-info">
            <div className="flex items-center gap-2 mb-3">
              <Truck size={16} className="text-[#0057FF]"/>
              <p className="overline">Shipping & delivery</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              {p.ships_from && (
                <div><p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Ships from</p><p className="mt-0.5">{p.ships_from}</p></div>
              )}
              {p.ships_to && (
                <div><p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Ships to</p><p className="mt-0.5">{p.ships_to}</p></div>
              )}
              {p.estimated_delivery && (
                <div><p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Estimated delivery</p><p className="mt-0.5">{p.estimated_delivery}</p></div>
              )}
            </div>
          </div>
        )}
      </div>

      <aside>
        <p className="overline mb-3 inline-flex items-center gap-1.5">
          <Package size={12}/>{p.product_type === "digital" ? "Digital download" : p.product_type}
        </p>
        <h1 className="h-display text-4xl">{p.title}</h1>
        <TranslatedText text={p.description || ""} className="text-[#4A4A4A] mt-4 whitespace-pre-line" testid="product-description"/>
        <Link to={`/u/${p.hooman?.id}`} className="mt-6 flex items-center gap-3 hover:opacity-90" data-testid="product-author">
          {p.hooman?.avatar && <img src={p.hooman.avatar} className="h-10 w-10 rounded-full object-cover" alt=""/>}
          <div>
            <p className="font-medium inline-flex items-center gap-1.5">{p.hooman?.name} {p.hooman?.is_verified && <BadgeCheck size={14}/>}</p>
            <p className="text-xs text-[#8A8A8A]">{(p.hooman?.categories?.length ? p.hooman.categories : [p.hooman?.category]).filter(Boolean).join(" · ")}</p>
          </div>
        </Link>

        <div className="card p-6 mt-6" data-testid="product-summary">
          {/* Price breakdown — buyer sees the full total (incl. Hoomanz Tip) before hitting Buy */}
          <div className="space-y-1.5 text-sm" data-testid="product-price-breakdown">
            <Row label="Subtotal" value={subtotal} testid="row-subtotal"/>
            {shipping > 0 && (
              <Row label="Shipping" value={shipping} testid="row-shipping"
                icon={<Truck size={12} className="text-[#8A8A8A]"/>}/>
            )}
            {isPhysical && quote?.shipping_free_applied && (
              <div className="text-xs text-[#0057FF]" data-testid="row-free-shipping">Free shipping applied.</div>
            )}
            {tax > 0 && (
              <Row label={`Tax${quote?.tax_rate ? ` (${quote.tax_rate}%)` : ""}`}
                value={tax} testid="row-tax"
                icon={<Receipt size={12} className="text-[#8A8A8A]"/>}/>
            )}
            {quote?.tax_included && (
              <div className="text-xs text-[#8A8A8A]" data-testid="row-tax-included">Tax already included in price.</div>
            )}
            {fee > 0 && (
              <div className="flex items-center justify-between" data-testid="row-fee">
                <span className="text-[#4A4A4A] inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#8A8A8A]"/>Hoomanz Tip (2%)</span>
                <span>+${fee.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-[#E5E5E3] pt-2 mt-2 flex items-baseline justify-between">
              <span className="font-medium">Total</span>
              <span className="font-heading text-3xl" data-testid="product-total">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
          <p className="text-xs text-[#4A4A4A] mt-2">Seller receives 100 % of their price. The 2 % Hoomanz Tip keeps this platform running.</p>
          {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}
          <button onClick={buy} disabled={busy} className="btn-primary w-full mt-5" data-testid="product-buy-btn">
            {busy ? "Opening checkout…" : <>Buy now <ArrowRight size={16}/></>}
          </button>
          {!user && (
            <p className="mt-2 text-[11px] text-[#8A8A8A] text-center">
              No account needed — pay as a guest, we&apos;ll email you the receipt & download link.
            </p>
          )}
          <p className="mt-3 text-xs text-[#4A4A4A] inline-flex items-center gap-1.5 justify-center w-full"><ShieldCheck size={12}/> Protected by Hoomanz</p>
        </div>
      </aside>
      {guestOpen && (
        <GuestEmailModal
          busy={busy}
          err={err}
          onClose={()=>{ setGuestOpen(false); setErr(""); }}
          onSubmit={(email)=>startCheckout(email)}
        />
      )}
    </div>
  );
}

function ProductGallery({ product }) {
  const imgs = (Array.isArray(product.images) && product.images.length ? product.images : (product.image ? [product.image] : [])).filter(Boolean);
  const [active, setActive] = useState(0);
  if (imgs.length === 0) return <div className="rounded-3xl overflow-hidden bg-[#F2F2F0] aspect-square" data-testid="product-gallery-empty"/>;
  const groupId = `product-${product.id}`;
  return (
    <div data-testid="product-gallery">
      <div className="rounded-3xl overflow-hidden bg-[#F2F2F0] aspect-square">
        <img src={imgs[active]} alt="" data-lightbox data-lightbox-group={groupId}
             className="w-full h-full object-cover cursor-zoom-in" data-testid="product-gallery-active"/>
      </div>
      {imgs.length > 1 && (
        <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-gallery-thumbs">
          {imgs.map((src, k) => (
            <button key={k} type="button" onClick={()=>setActive(k)}
              className={`aspect-square rounded-xl overflow-hidden bg-[#F2F2F0] ring-1 transition-all
                ${k===active ? "ring-2 ring-[#0A0A0A]" : "ring-[#E5E5E3] hover:ring-[#0A0A0A]"}`}
              data-testid={`product-gallery-thumb-${k}`}>
              <img src={src} alt="" className="w-full h-full object-cover"/>
            </button>
          ))}
        </div>
      )}
      {/* Hidden lightbox anchors so viewers can swipe through all photos */}
      {imgs.slice(1).map((src, k) => (
        <img key={`lb-${k}`} src={src} alt="" data-lightbox data-lightbox-group={groupId} className="hidden"/>
      ))}
    </div>
  );
}

function GuestEmailModal({ busy, err, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !/^\S+@\S+\.\S+$/.test(v)) return;
    onSubmit(v);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="guest-checkout-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-6 max-w-sm w-full">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Guest checkout</p>
          <button type="button" onClick={onClose} aria-label="Close"><X size={16}/></button>
        </div>
        <h3 className="font-heading text-2xl mb-1">Where should we send your receipt?</h3>
        <p className="text-sm text-[#4A4A4A] mb-4">
          We&apos;ll email you the receipt and a private download / booking link.
          No password required.
        </p>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A] inline-flex items-center gap-1.5">
            <Mail size={11}/> Your email
          </span>
          <input
            type="email"
            required
            value={email}
            onChange={(e)=>setEmail(e.target.value)}
            placeholder="you@example.com"
            className="input mt-1.5"
            autoFocus
            data-testid="guest-email-input"
          />
        </label>
        {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="guest-email-err">{err}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1"
                  data-testid="guest-email-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1"
                  data-testid="guest-email-continue">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Opening…</> : <>Continue <ArrowRight size={14}/></>}
          </button>
        </div>
        <p className="text-[11px] text-[#8A8A8A] mt-3 text-center">
          Already have an account? <Link to="/login" className="underline">Log in instead</Link>.
        </p>
      </form>
    </div>
  );
}

function Row({ label, value, icon, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-[#4A4A4A] inline-flex items-center gap-1.5">{icon}{label}</span>
      <span>${Number(value || 0).toFixed(2)}</span>
    </div>
  );
}
